import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CarrierOperator,
  type UpdateOperatorVars,
  useCarrierDispatchCenters,
  useUpdateOperator,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";
import {
  operatorDisplayName,
  operatorNameValidationError,
  validOperatorName,
} from "@/lib/operator-identity";

interface EditOperatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operator: CarrierOperator;
}

const STATUS_OPTIONS = ["ON_DUTY", "OFF_DUTY", "BLOCKED"] as const;

/**
 * Edit dialog for a single operator. Surface restricted to the server's
 * allowlist subset that matters day-to-day. Name updates the canonical
 * CarrierUser identity through the scoped carrier contract; email remains
 * read-only account information.
 */
export function EditOperatorDialog({
  open,
  onOpenChange,
  operator,
}: EditOperatorDialogProps) {
  const update = useUpdateOperator();
  const submissionInFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const dispatchCenters = useCarrierDispatchCenters();
  const currentDispatchCenterId =
    operator.dispatchCenterId ?? operator.depot?.dispatchCenterId ?? "";
  const [status, setStatus] = useState<string>(operator.status);
  const currentName = validOperatorName(operator) ?? "";
  const [name, setName] = useState(currentName);
  const [nameError, setNameError] = useState<string | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<string>(
    operator.dateOfBirth ? operator.dateOfBirth.slice(0, 10) : ""
  );
  const [dispatchCenterId, setDispatchCenterId] = useState<string>(
    currentDispatchCenterId
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionInFlight.current) return;
    const normalizedName = name.trim();
    const nextNameError = operatorNameValidationError(normalizedName);
    if (nextNameError) {
      setNameError(nextNameError);
      return;
    }
    submissionInFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);
    const body: UpdateOperatorVars["body"] = {};
    if (normalizedName !== currentName) body.name = normalizedName;
    if (status !== operator.status) body.status = status;
    if (dateOfBirth !== (operator.dateOfBirth?.slice(0, 10) ?? "")) {
      if (dateOfBirth) body.dateOfBirth = dateOfBirth;
    }
    if (dispatchCenterId && dispatchCenterId !== currentDispatchCenterId) {
      body.dispatchCenterId = dispatchCenterId;
    }
    try {
      await update.mutateAsync({ operatorId: operator.id, body });
      toast.success("Operator updated.");
      onOpenChange(false);
    } catch (error) {
      setSubmitError(
        hasHttpStatus(error, 403)
          ? "You do not have permission to update this operator."
          : "Failed to update operator. Try again."
      );
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  };

  const pending = update.isPending || submitting;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) return;
        onOpenChange(next);
      }}
      title={`Edit operator — ${operatorDisplayName(operator)}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="op-name" className="mb-1 block text-sm font-medium">
            Name
          </label>
          <Input
            id="op-name"
            value={name}
            required
            maxLength={120}
            aria-invalid={nameError ? "true" : undefined}
            aria-describedby={nameError ? "op-name-error" : undefined}
            onChange={(event) => {
              setName(event.target.value);
              if (nameError) setNameError(null);
            }}
            disabled={pending}
          />
          {nameError && (
            <p
              id="op-name-error"
              role="alert"
              className="mt-1 text-sm text-destructive"
            >
              {nameError}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="op-status" className="block text-sm font-medium mb-1">
            Status
          </label>
          <NativeSelect
            id="op-status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            disabled={pending}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option.replace(/_/g, " ")}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div>
          <label
            htmlFor="op-dispatch-center"
            className="block text-sm font-medium mb-1"
          >
            Dispatch center
          </label>
          <NativeSelect
            id="op-dispatch-center"
            value={dispatchCenterId}
            onChange={(event) => setDispatchCenterId(event.target.value)}
            disabled={pending || dispatchCenters.isLoading}
          >
            <option value="">Select dispatch center...</option>
            {(dispatchCenters.data ?? [])
              .filter((center) => center.isActive)
              .map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
          </NativeSelect>
        </div>

        <div>
          <label htmlFor="op-dob" className="block text-sm font-medium mb-1">
            Date of birth
          </label>
          <Input
            id="op-dob"
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            disabled={pending}
          />
        </div>

        {(submitError || update.isError) && (
          <p
            role="alert"
            aria-label="Operator update failed"
            className="text-sm text-destructive"
          >
            {submitError ?? "Failed to update operator. Try again."}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="touch"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button type="submit" size="touch" disabled={pending}>
            {pending ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
